import { useCallback } from 'react'
import type { ContextUploadFileItem } from '~/components/chat/message'
import { useGlobalStreaming } from '~/contexts/GlobalStreamingContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { workspaceChatStart, CanvasContextInfo } from '~/services/chatService'

export const useStreamingChat = (conversationUuid?: string) => {
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = currentWorkspace?.uuid
  const globalStreaming = useGlobalStreaming()
  const conversationState = globalStreaming.getConversationState(conversationUuid || '')
  const messages = conversationState.messages || []
  const isStreaming = conversationState.isStreaming

  const sendMessage = useCallback(
    async (
      message: string,
      executionMode?: string,
      verificationMode?: string,
      contextUpload?: ContextUploadFileItem[],
      canvasContextInfo?: CanvasContextInfo[],
      isSpeechInput?: boolean
    ) => {
      if (!workspaceUuid) {
        console.error('[useStreamingChat] Cannot send message: workspace UUID is required')
        return
      }
      if (!conversationUuid) {
        console.error('[useStreamingChat] Cannot send message: conversation UUID is required')
        return
      }
      if (isSpeechInput) {
        return globalStreaming.sendMessageInternal(
          conversationUuid,
          message,
          workspaceUuid,
          executionMode,
          verificationMode,
          contextUpload,
          canvasContextInfo,
          true
        )
      }

      return globalStreaming.sendMessageInternal(
        conversationUuid,
        message,
        workspaceUuid,
        executionMode,
        verificationMode,
        contextUpload,
        canvasContextInfo
      )
    },
    [globalStreaming, workspaceUuid, conversationUuid]
  )

  const startWorkspaceChat = useCallback(
    async (options?: {
      mode?: string
      repository_branches?: Array<{ conn_type: string; repository: string; branch: string }>
    }) => {
      if (!workspaceUuid) {
        console.error('[useStreamingChat] Cannot start conversation: workspace UUID is required')
        return null
      }

      try {
        const conversation = await workspaceChatStart(workspaceUuid, {
          mode: options?.mode,
          repository_branches: options?.repository_branches
        })
        return conversation
      } catch (error) {
        console.error('[useStreamingChat] Failed to create conversation:', error)
        return null
      }
    },
    [workspaceUuid]
  )

  return {
    messages,
    isStreaming,
    sendMessage,
    startWorkspaceChat
  }
}
